import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  SupplierApplicationsActions,
  SupplierDashboardActions,
  ViewSupplierApplicationActions,
  RevokeSupplierPrequalificationActions,
} from './supplier-dashboard.actions';
import { SupplierDashboardResponse } from '../responses/supplier-dashboard.response';
import { GET_SUPPLIER_DASHBOARD_QUERY } from '../graphql/supplier-dashboard.graphql';
import {
  LIST_SUPPLIER_APPLICATIONS,
  VIEW_SUPPLIER_APPLICATION,
  REVOKE_SUPPLIERPREQUALIFICATION,
} from '../graphql/supplier-applications.graphql';
import {
  ListSupplierApplicationsResponse,
  SupplierApplication,
  ViewSupplierApplicationResponse,
  RevokeSupplierPrequalificationResponse,
} from '../responses/supplier-applications.response';
import { PageableResponse } from '@modules/financial-security-configuration/responses/get-financial-security.response';

@Injectable({
  providedIn: 'root',
})
export class SupplierDashboardEffects {
  constructor(
    private actions$: Actions,
    private graphQlService: GraphQlService,
  ) {}

  private createEmptyApplicationsPage(size: number): PageableResponse<SupplierApplication> {
    return {
      first: true,
      hasContent: false,
      hasNext: false,
      hasPrevious: false,
      last: true,
      number: 0,
      numberOfElements: 0,
      size,
      totalElements: 0,
      totalPages: 0,
      content: [],
    };
  }

  loadDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupplierDashboardActions.request),
      switchMap(() =>
        this.graphQlService
          .query<SupplierDashboardResponse>({
            query: GET_SUPPLIER_DASHBOARD_QUERY,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data?.getSupplierDashboard;
              if (!payload?.status) {
                return SupplierDashboardActions.failure({
                  error: {
                    message: payload?.message || 'Failed to load dashboard',
                    data: payload,
                  },
                });
              }

              return SupplierDashboardActions.success({ response: payload.data ?? null });
            }),
            catchError((error) =>
              of(
                SupplierDashboardActions.failure({
                  error: {
                    message: error?.message || 'Failed to load dashboard',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadSupplierApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SupplierApplicationsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<ListSupplierApplicationsResponse>({
            query: LIST_SUPPLIER_APPLICATIONS,
            variables: {
              pageableParam: request,
            },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) =>
              SupplierApplicationsActions.success({
                response:
                  response?.data?.listSupplierPreQualificationsByCurrentUser ??
                  this.createEmptyApplicationsPage(request.size ?? 10),
              }),
            ),
            catchError((error) =>
              of(
                SupplierApplicationsActions.failure({
                  error: {
                    message: error?.message || 'Failed to load supplier applications',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  viewSupplierApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ViewSupplierApplicationActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<ViewSupplierApplicationResponse>({
            query: VIEW_SUPPLIER_APPLICATION,
            variables: {
              uuid: request,
            },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response?.data?.getSupplierPreQualificationByUuid;
              if (!payload?.status) {
                return ViewSupplierApplicationActions.failure({
                  error: {
                    message: payload?.message || 'Failed to load supplier application',
                    data: payload,
                  },
                });
              }

              return ViewSupplierApplicationActions.success({ response: payload.data });
            }),
            catchError((error) =>
              of(
                ViewSupplierApplicationActions.failure({
                  error: {
                    message: error?.message || 'Failed to load supplier application',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  revokeSupplierPrequalification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RevokeSupplierPrequalificationActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<RevokeSupplierPrequalificationResponse>({
            mutation: REVOKE_SUPPLIERPREQUALIFICATION,
            variables: {
              preQualificationUuid: request.preQualificationUuid,
            },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response?.data?.revokeSupplierPrequalificationToDraft;
              if (!payload?.status) {
                return RevokeSupplierPrequalificationActions.failure({
                  error: {
                    message: payload?.message || 'Failed to revoke application',
                    data: payload,
                  },
                });
              }

              return RevokeSupplierPrequalificationActions.success({ response: response.data });
            }),
            catchError((error) =>
              of(
                RevokeSupplierPrequalificationActions.failure({
                  error: {
                    message: error?.message || 'Failed to revoke application',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
